import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import {AbstractListView} from "../abstract-list-view";
import {IModel} from "../../../../../shared/view-model/imodel.interface";
import {FormControl} from "@angular/forms";
import {SessionActions} from "../../../../../shared/actions/session.actions";
import {isUndefined} from "util";
import {DatatableComponent as SwimlaneDatatableComponent} from "@swimlane/ngx-datatable";
import {ActivatedRoute, Router} from "@angular/router";
import {debounceTime} from "rxjs/operators";
import {Page} from '../../../../../shared/view-model/core/page.model';
import {SortInfo} from '../../../../../shared/view-model/core/sort.model';

@Component({
  selector: 'vb-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.css'],
  inputs: ['messages', 'isLoading'],
})
export class DatatableComponent extends AbstractListView implements OnInit, AfterViewInit, OnChanges {

  @ViewChild('formatCurrencyTmpl')
  public formatCurrencyTmpl: TemplateRef<any>;

  @ViewChild('formatDateTmpl')
  public formatDateTmpl: TemplateRef<any>;

  @ViewChild('formatDateTimeTmpl')
  public formatDateTimeTmpl: TemplateRef<any>;

  @ViewChild('dataTable')
  public table: SwimlaneDatatableComponent;

  @Input()
  public rowIdentity = (row) => {
    return row.cuid;
  };

  // Table templates
  @Input()
  @ViewChild('actionsTmpl')
  actionsTmpl: TemplateRef<any>;

  // Paging and sorting

  @Input()
  externalPaging = false;

  @Input()
  externalSorting = false;

  @Input()
  page = new Page();

  @Input()
  sort = new SortInfo();

  @Output()
  pageChange: EventEmitter<Page> = new EventEmitter();

  @Output()
  sortChange: EventEmitter<SortInfo> = new EventEmitter();

  @ViewChild('detailToggleTmpl')
  detailToggleTmpl: TemplateRef<any>;

  @Input()
  checkEditableFunction: (item: IModel) => boolean;

  @Input()
  dataLabel: string = "Datensatz";

  @Input()
  filterControl: FormControl;

  @Input()
  hasDetails: boolean = false;

  @Input()
  editable: boolean = true;

  @Input()
  selectable: boolean = false;

  @Input()
  addSelectableColumn: boolean = true;

  selectedItems: any[] = [];

  @Input()
  tableColumns = [{prop: 'label', name: 'Bezeichner'}];

  @Input()
  set data(data: IModel[]) {
    this.rows = data;
    if (this.filterControl) {
      const filterVal = this.filterControl.value;
      this.filterItems(filterVal);
    }
  }

  get data(): IModel[] {
    return this.rows;
  }


  @Input()
  rowDetailTmpl: TemplateRef<any>;

  @Input()
  rowClass: (row) => any;

  @Input()
  rowDetailToggleClicked: (row, expanded, event: Event) => void = this.toggleExpandRow;

  @Output()
  detailToggleClicked: EventEmitter<{ event: Event, row: IModel, expanded: boolean }> = new EventEmitter();

  @Output()
  editClicked: EventEmitter<IModel> = new EventEmitter<IModel>();

  @Output()
  deleteClicked: EventEmitter<IModel> = new EventEmitter<IModel>();

  @Output()
  cellUpdated: EventEmitter<{ cuid: string, data: any }> = new EventEmitter();

  @Output()
  activate: EventEmitter<any> = new EventEmitter();

  @Output()
  rowSelect: EventEmitter<any[]> = new EventEmitter();

  @Output()
  itemSelect: EventEmitter<any[]> = new EventEmitter();

  @Output()
  detailToggle: EventEmitter<any> = new EventEmitter();

  constructor(sessionActions: SessionActions, private route: ActivatedRoute, private router: Router, cd: ChangeDetectorRef) {
    super(sessionActions, cd);
  }

  canEdit(item: IModel) {
    if (this.checkEditableFunction) {
      return this.checkEditableFunction(item);
    } else {
      return !item || isUndefined(item.is_editable) ? false : item.is_editable;
    }
  }

  ngOnInit() {
    this.columns = this.buildColumns();

    this.dataSubscriptions.push(this.route.queryParams.subscribe(param => {
      this.setPage({offset: param.page ? (+param.page - 1) : 0});
    }));
  }

  resetToFirstPage() {
    if (!this.externalPaging) {
      const page = this.route.snapshot.queryParams.page;
      this.page = new Page();
      this.page.totalElements = this.rows.length;
      this.table.bodyComponent.updateOffsetY(this.page.pageNumber);
      this.table.offset = 0;
      this.setPage({offset: 0});
    } else {
      this.table.bodyComponent.updateOffsetY(0);
      this.table.offset = 0;
      this.setPage({offset: 0});
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngOnChanges(changes: SimpleChanges): void {

    let tblCol = changes['tableColumns'];

    if (tblCol) {
      this.columns = this.buildColumns();
    }

    if (changes['filterControl']) {
      if (changes['filterControl'].previousValue) {
        changes['filterControl'].previousValue.valueChanges.unsubscribe();
      }
      if (this.filterControl) {
        this.filterControl.valueChanges.pipe(
          debounceTime(500)
        ).subscribe((term: any) => {
          this.filterItems(term);
          this.resetToFirstPage();
        });
      }
    }

  }

  setPage(pageInfo) {
    this.page.pageNumber = pageInfo.offset;
    this.router.navigate(['./'], {
      relativeTo: this.route, queryParams: {page: pageInfo.offset + 1},
      replaceUrl: true
    });
    this.pageChange.emit(this.page);
  }

  onSort(event) {
    this.sort = new SortInfo(event.sorts[0].prop, event.sorts[0].dir);
    this.sortChange.emit(this.sort);
  }

  private buildColumns() {
    let col = [];

    if (this.selectable && this.addSelectableColumn) {
      col.push({
        name: '',
        width: 30,
        sortable: false,
        canAutoResize: false,
        draggable: false,
        resizeable: false,
        headerCheckboxable: true,
        checkboxable: true
      });
    }

    if (this.hasDetails) {
      col.push({
        name: '',
        cellTemplate: this.detailToggleTmpl,
        sortable: false,
        draggable: false,
        resizeable: false,
        canAutoResize: false,
        width: 50
      });
    }

    col.push(...this.tableColumns);

    if (this.editable) {
      col.push({
        name: '',
        cellTemplate: this.actionsTmpl,
        sortable: false,
        resizeable: false,
        draggable: false,
        canAutoResize: false,
        width: 150
      });
    }

    return col;
  }

  edit(item: IModel) {
    this.editClicked.emit(item);
  }

  delete(item: IModel) {
    this.deleteClicked.emit(item);
  }

  toggleExpandRow(row, expanded, event: Event) {
    this.table.rowDetail.toggleExpandRow(row);
    this.detailToggleClicked.emit({event: event, row: row, expanded: expanded});
  }

  filterItems(searchTerm) {
    if (!searchTerm || searchTerm == "") {
      this.rows = this.data;
    } else {
      if (this.data) {
        this.rows = this.data.filter((si: IModel) => this.findStringInTable(si, this.columns, searchTerm));
      }
    }
  }

  recalculate() {
    this.table.recalculate();
    this.cd.detectChanges();
  }

  onCellUpdate(row, res, index) {
    this.cellUpdated.emit({cuid: row.cuid, data: res});
  }

  onActivate(event) {
    this.activate.emit(event);
  }

  onSelect({selected}) {

    if (isUndefined(selected)) {
      return;
    }

    // Mutable version
    //this.selectedItems.splice(0, this.selectedItems.length);
    //this.selectedItems.push(...selected);

    this.selectedItems = [...selected];
    this.rowSelect.emit(this.selectedItems.map(item => item ? item.cuid : item));
    this.itemSelect.emit(this.selectedItems);

  }

  onDetailToggle() {
    this.detailToggle.emit();
  }

}



// WEBPACK FOOTER //
// ./src/app/core/visbos-core/ui/components/datatable/datatable.component.ts